import * as Icons from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { App as AntdApp } from 'antd';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  HeaderNoticeEntry,
  PoolSwitcher,
  SubSystemEntry,
} from '@/components';
import { getMenuPermissions } from '@/pages-pool-member/system/role/services/api';
import LangtanSvc from '@/services/langtan';
import { queryPoolSwitcherList } from '@/services/langtan/iotProject';
import type { MenuPermissionItem } from '@/utils/permission';
import {
  buildMenuTree,
  getAllMenusFromStorage,
  savePermissionsToStorage,
} from '@/utils/permission';
import defaultSettings from '../config/defaultSettings';
import {
  hasSubsystemPermission,
  isPoolSystemMenuPath,
  pickMemberMenus,
  pickSubsystemChildrenMenus,
  pickSystemSettingsMenus,
  resolveFirstAccessiblePath,
  resolveSubsystemCodeByPath,
} from './config/poolSystemMenu';
import {
  RETIRED_TRAINING_MENU_CODES,
  RETIRED_TRAINING_MENU_PATHS,
} from './config/poolTrainingMenu';
import { GoodsDayTypeEnum, MEMBER_SERVICE_BASE_URL } from './constants';
import { requestConfig } from './requestConfig';
import './tailwind.css';
import { splitHours } from './utils';
import {
  getCurrentPoolId,
  normalizePoolId,
  poolIdEquals,
  setCurrentPoolId,
} from './utils/currentPool';
import { normalizeSwimStaffDetailVo } from './utils/normalizeStaffDetail';
import { storageAuth } from './utils/storage';

const loginPath = '/user/login';
const publicPaths = [
  loginPath,
  '/user/register',
  '/user/register-result',
  '/screen/pool-monitor',
];

function initRuntimeDiagnostics() {
  if (process.env.NODE_ENV !== 'development') return;
  const w = window as any;
  if (w.__LT_RUNTIME_DIAG_INSTALLED__) return;
  w.__LT_RUNTIME_DIAG_INSTALLED__ = true;

  const getRendererInfo = () => {
    try {
      const hook = w.__REACT_DEVTOOLS_GLOBAL_HOOK__;
      const renderers = hook?.renderers;
      if (!renderers || typeof renderers.forEach !== 'function') {
        return [];
      }
      const list: Array<Record<string, unknown>> = [];
      renderers.forEach((renderer: any, id: any) => {
        list.push({
          id,
          version: renderer?.version,
          rendererPackageName: renderer?.rendererPackageName,
        });
      });
      return list;
    } catch {
      return [];
    }
  };

  const printDiagnostic = (title: string, payload: Record<string, unknown>) => {
    // 统一日志前缀，便于从控制台快速过滤定位
    console.groupCollapsed(`[runtime-diag] ${title}`);
    Object.entries(payload).forEach(([k, v]) => {
      console.log(`${k}:`, v);
    });
    console.groupEnd();
  };

  const shouldReport = (text: string) =>
    /Invalid hook call|useContext|Cannot read properties of null/i.test(text);

  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    try {
      const text = args
        .map((item) => (typeof item === 'string' ? item : item?.message || ''))
        .join(' ');
      if (shouldReport(text)) {
        printDiagnostic('console.error 捕获到疑似 Hook 异常', {
          route: `${window.location.pathname}${window.location.search}`,
          text,
          stack: args.find((x) => x?.stack)?.stack || new Error().stack,
          reactVersion: React.version,
          renderers: getRendererInfo(),
        });
      }
    } catch {
      // ignore diagnostic failure
    }
    originalConsoleError(...args);
  };

  window.addEventListener('error', (event) => {
    const text = String(event.error?.message || event.message || '');
    if (!shouldReport(text)) return;
    printDiagnostic('window.error 捕获到异常', {
      route: `${window.location.pathname}${window.location.search}`,
      text,
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      reactVersion: React.version,
      renderers: getRendererInfo(),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as any;
    const text = String(reason?.message || reason || '');
    if (!shouldReport(text)) return;
    printDiagnostic('unhandledrejection 捕获到异常', {
      route: `${window.location.pathname}${window.location.search}`,
      text,
      stack: reason?.stack,
      reactVersion: React.version,
      renderers: getRendererInfo(),
    });
  });

  // 诊断 useContext 被非法调用的位置（只在 dev 注入）
  try {
    const reactAny = React as any;
    if (!reactAny.__LT_PATCHED_USE_CONTEXT__) {
      const originalUseContext = reactAny.useContext;
      reactAny.useContext = (...args: any[]) => {
        try {
          return originalUseContext(...args);
        } catch (error: any) {
          const text = String(error?.message || error || '');
          if (shouldReport(text)) {
            printDiagnostic(
              'React.useContext 调用异常（疑似违规 Hook 调用点）',
              {
                route: `${window.location.pathname}${window.location.search}`,
                text,
                stack: error?.stack || new Error().stack,
                reactVersion: React.version,
              },
            );
          }
          throw error;
        }
      };
      reactAny.__LT_PATCHED_USE_CONTEXT__ = true;
    }
  } catch {
    // ignore patch failure
  }
}

initRuntimeDiagnostics();

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.SwimStaffDetailVo;
  loading?: boolean;
  effectiveDayly?: {
    [GoodsDayTypeEnum.Worker]: number[];
    [GoodsDayTypeEnum.Holiday]: number[];
  };
  pools?: API.SwimPoolListVo[];
  /** 雪花 ID，必须用字符串 */
  currentPoolId?: string;
  menuData?: MenuPermissionItem[]; // 新增：动态菜单数据
  fetchUserInfo?: () => Promise<API.SwimStaffDetailVo | undefined>;
}> {
  const currentPath = history.location?.pathname || window.location.pathname;
  const isPublicPath = publicPaths.includes(currentPath);
  const staffId = storageAuth.get()?.staffId;
  if (!staffId && !isPublicPath) {
    history.push(loginPath);
  }
  if (!staffId && isPublicPath) {
    return {
      settings: defaultSettings as Partial<LayoutSettings>,
      fetchUserInfo: async () => undefined,
    };
  }
  const fetchUserInfo = async () => {
    const msg = await LangtanSvc.getStaffDetail({
      id: staffId,
    });
    return (
      normalizeSwimStaffDetailVo(msg.data as API.SwimStaffDetailVo) ?? msg.data
    );
  };

  /**
   * 获取用户权限数据（菜单和按钮）
   * @param roleId 用户角色ID
   */
  const fetchPermissions = async (roleId: number) => {
    try {
      const res = await getMenuPermissions(roleId);
      if (res.code === 0 && res.data) {
        // 保存权限数据到 LocalStorage
        savePermissionsToStorage(res.data);
        // 获取所有菜单并构建树形结构
        const menus = getAllMenusFromStorage();
        const menuTree = buildMenuTree(menus);
        return menuTree;
      }
    } catch (error) {
      console.error('获取用户权限失败:', error);
    }
    return [];
  };

  const fetchEffectiveDayly = async () => {
    try {
      const timeRange: any = {
        [GoodsDayTypeEnum.Worker]: [],
        [GoodsDayTypeEnum.Holiday]: [],
      };
      const res = await LangtanSvc.getEffectiveDaylyVersionDetail();
      res.data?.businessTimeList?.forEach((item) => {
        if (
          item.dayType === GoodsDayTypeEnum.Worker &&
          item.timeStart != null &&
          item.timeEnd != null
        ) {
          timeRange[GoodsDayTypeEnum.Worker] = splitHours(
            item.timeStart,
            item.timeEnd,
          );
        }
        if (
          item.dayType === GoodsDayTypeEnum.Holiday &&
          item.timeStart != null &&
          item.timeEnd != null
        ) {
          timeRange[GoodsDayTypeEnum.Holiday] = splitHours(
            item.timeStart,
            item.timeEnd,
          );
        }
      });
      return timeRange;
    } catch (error) {
      console.error('---EffectiveDayly---', error);
    }
  };

  const fetchPools = async (): Promise<API.SwimPoolListVo[]> => {
    try {
      const msg = await queryPoolSwitcherList();
      if (msg?.code === 0 && Array.isArray(msg.data)) {
        return msg.data.map((item) => ({
          id: item.id != null ? String(item.id) : undefined,
          poolName: item.poolName,
          gymId: item.gymId != null ? String(item.gymId) : undefined,
          gymName: item.gymName,
          spaceId: item.spaceId != null ? String(item.spaceId) : undefined,
          spaceName: item.spaceName,
          cameraCount:
            typeof item.cameraCount === 'number'
              ? item.cameraCount
              : Number(item.cameraCount) || 0,
        })) as API.SwimPoolListVo[];
      }
    } catch (error) {
      console.error('加载泳池切换列表失败', error);
    }
    return [];
  };

  const resolveCurrentPoolId = (pools?: API.SwimPoolListVo[]) => {
    const stored = getCurrentPoolId();
    const withCameras = (list?: API.SwimPoolListVo[]) =>
      list?.find((p) => Number(p.cameraCount) > 0);

    if (stored != null && pools?.some((p) => poolIdEquals(p.id, stored))) {
      const storedPool = pools.find((p) => poolIdEquals(p.id, stored));
      // 已选池若无安全摄像机，而其它池有，则迁移到有摄像机的池（避免识别/告警默认空）
      if (Number(storedPool?.cameraCount) > 0) {
        return stored;
      }
      const preferred = withCameras(pools);
      const preferredId = normalizePoolId(preferred?.id);
      if (preferredId != null) {
        setCurrentPoolId(preferredId);
        return preferredId;
      }
      return stored;
    }

    // 默认优先有安全摄像机的泳池
    const preferred = withCameras(pools) ?? pools?.[0];
    const firstId = normalizePoolId(preferred?.id);
    if (firstId != null) {
      setCurrentPoolId(firstId);
      return firstId;
    }
    return stored;
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (!publicPaths.includes(location.pathname)) {
    const currentUser = await fetchUserInfo();
    const effectiveDayly = await fetchEffectiveDayly();
    const pools = await fetchPools();
    const currentPoolId = resolveCurrentPoolId(pools);

    // 获取权限数据（根据角色ID）
    let menuData: MenuPermissionItem[] = [];
    if (currentUser?.roleId) {
      menuData = await fetchPermissions(currentUser.roleId);
    }

    return {
      fetchUserInfo,
      currentUser,
      effectiveDayly,
      pools,
      currentPoolId,
      menuData, // 返回菜单数据
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export function rootContainer(container: React.ReactNode) {
  return <AntdApp>{container}</AntdApp>;
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    actionsRender: () => [<HeaderNoticeEntry key="header-notice" />],
    avatarProps: {
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // 在 header 中间渲染子系统入口
    headerContentRender: () => <SubSystemEntry />,
    footerRender: () => (
      <div>
        <Footer />
      </div>
    ),
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        !publicPaths.includes(location.pathname)
      ) {
        history.push(loginPath);
        return;
      }
      // 无对应子系统授权时，禁止停留在该产品路径（顶栏已隐藏仍可能手输 URL）
      if (
        initialState?.currentUser &&
        !publicPaths.includes(location.pathname)
      ) {
        const code = resolveSubsystemCodeByPath(location.pathname);
        const menus = (initialState.menuData || []) as MenuPermissionItem[];
        if (menus.length > 0 && !hasSubsystemPermission(code, menus)) {
          const fallback = resolveFirstAccessiblePath(menus);
          if (!fallback) {
            return;
          }
          if (location.pathname !== fallback) {
            history.replace(fallback);
          }
        }
      }
    },
    // 动态侧栏：五个子系统均按权限树切分
    menu: {
      params: initialState,
      locale: false,
      request: async () => {
        // 如果已经有菜单数据，直接使用
        if (initialState?.menuData && initialState.menuData.length > 0) {
          return convertMenuTreeToAntdMenu(initialState.menuData);
        }
        // 否则从缓存加载
        const menus = getAllMenusFromStorage();
        if (menus.length > 0) {
          const menuTree = buildMenuTree(menus);
          return convertMenuTreeToAntdMenu(menuTree);
        }
        return [];
      },
    },
    menuDataRender: (menuData) => {
      const pathname = history.location.pathname;
      if (isPoolSystemMenuPath(pathname)) {
        return pickSystemSettingsMenus(menuData);
      }
      if (pathname.startsWith('/member')) {
        return pickMemberMenus(menuData);
      }
      // 安全 / 设备 / 培训 / 其它：按 SUBSYSTEM 从权限树切侧栏
      const code = resolveSubsystemCodeByPath(pathname);
      return pickSubsystemChildrenMenus(menuData, code);
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    // 仅设备/安全且多泳池时挂切换器；否则关掉侧栏 header 槽，避免顶部空白
    menuHeaderRender: () => {
      const pathname = history.location.pathname;
      const onPoolScoped =
        pathname.startsWith('/device') || pathname.startsWith('/safety');
      const poolCount = initialState?.pools?.length ?? 0;
      if (!onPoolScoped || poolCount <= 1) {
        return false;
      }
      return <PoolSwitcher />;
    },
    childrenRender: (children) => <>{children}</>,
    ...initialState?.settings,
  };
};

/**
 * 将图标字符串转换为 React 图标组件
 * @param iconName 图标名称字符串（如 "UserOutlined"）
 * @param useDefault 是否使用默认图标，默认为 true
 * @returns React 图标组件、默认图标或 undefined
 */
function getIconComponent(iconName?: string): React.ReactNode {
  // 如果没有提供图标名称
  if (!iconName) {
    return undefined;
  }

  // 从 @ant-design/icons 中动态获取图标组件
  const IconComponent = (Icons as any)[iconName];

  if (IconComponent) {
    return React.createElement(IconComponent);
  }
  console.warn(`图标 "${iconName}" 未找到`);
  // return React.createElement(Icons.AppstoreOutlined);
  return undefined;
}

/**
 * 将菜单树转换为 Ant Design Pro Layout 需要的格式
 * @param menuTree 菜单树数据
 * @returns Ant Design Pro Layout 菜单配置
 */
function convertMenuTreeToAntdMenu(menuTree: any[]): any[] {
  const normalizeMenuPath = (path?: string) => {
    if (!path) {
      return path;
    }
    return path.trim();
  };
  const normalizeMenuComponent = (component?: string, path?: string) => {
    if (!component) {
      return component;
    }
    let normalized = component.trim();
    if (normalized.startsWith('@/')) {
      normalized = normalized.slice(2);
    }
    if (normalized.startsWith('./')) {
      normalized = normalized.slice(2);
    }

    const normalizedPath = path?.trim() || '';
    const pathWithoutPrefix = normalized
      .replace(/^pages-pool-member\//, '')
      .replace(/^pages-pool-safety\//, '')
      .replace(/^pages-pool-device\//, '')
      .replace(/^pages-pool-training\//, '')
      .replace(/^pages\//, '');

    // /member 菜单优先映射 pages-pool-member 目录
    if (normalizedPath.startsWith('/member')) {
      return `pages-pool-member/${pathWithoutPrefix}`;
    }
    // /project/basic、/project/system 归属项目配置，组件仍在 pages-pool-member
    if (
      normalizedPath.startsWith('/project/basic') ||
      normalizedPath.startsWith('/project/system')
    ) {
      return `pages-pool-member/${pathWithoutPrefix}`;
    }
    // /system/space 旧前缀兼容
    if (normalizedPath.startsWith('/system/space')) {
      return `pages-pool-member/${pathWithoutPrefix}`;
    }
    // /safety 菜单优先映射 pages-pool-safety 目录
    if (normalizedPath.startsWith('/safety')) {
      return `pages-pool-safety/${pathWithoutPrefix}`;
    }
    // /device 菜单优先映射 pages-pool-device 目录
    if (normalizedPath.startsWith('/device')) {
      return `pages-pool-device/${pathWithoutPrefix}`;
    }
    // /training 菜单优先映射 pages-pool-training 目录
    if (normalizedPath.startsWith('/training')) {
      return `pages-pool-training/${pathWithoutPrefix}`;
    }

    if (normalized.startsWith('pages-pool-member/')) return normalized;
    if (normalized.startsWith('pages-pool-safety/')) return normalized;
    if (normalized.startsWith('pages-pool-device/')) return normalized;
    if (normalized.startsWith('pages-pool-training/')) return normalized;
    if (normalized.startsWith('pages/')) return normalized;
    return normalized;
  };

  return menuTree
    .filter((menu: any) => {
      const path = String(menu.path || '').trim();
      const code = String(menu.code || '').trim();
      if (
        RETIRED_TRAINING_MENU_PATHS.includes(path) ||
        RETIRED_TRAINING_MENU_CODES.includes(code)
      ) {
        return false;
      }
      if (menu.status == null || menu.status === '') return true;
      return Number(menu.status) === 1;
    })
    .map((menu: any) => {
      const normalizedPath = normalizeMenuPath(menu.path);
      const normalizedComponent = normalizeMenuComponent(
        menu.component,
        normalizedPath,
      );
      if (
        process.env.NODE_ENV === 'development' &&
        menu.component &&
        normalizedComponent !== menu.component
      ) {
        console.info('[menu-route-compat]', {
          code: menu.code,
          rawPath: menu.path,
          normalizedPath,
          rawComponent: menu.component,
          normalizedComponent,
        });
      }

      const menuItem: any = {
        id: menu.id,
        parentId: menu.parentId,
        sortOrder: menu.sortOrder,
        path: normalizedPath,
        name: menu.name,
        icon: getIconComponent(menu.icon),
        hideInMenu: menu.isHidden === 1, // 设置是否在左侧菜单中隐藏
        access: menu.code, // 使用菜单 code 作为 access 权限标识
        code: menu.code,
        type: menu.type,
      };

      // /safety 由 safetyRouteProxy 统一承载，勿把后端旧 component 注册进菜单路由
      if (!normalizedPath?.startsWith('/safety')) {
        menuItem.component = normalizedComponent;
      }

      // 如果有子菜单，递归转换
      if (menu.children && menu.children.length > 0) {
        const convertedChildren = convertMenuTreeToAntdMenu(menu.children);
        // 只有转换后的子菜单不为空时，才添加 children 属性
        if (convertedChildren.length > 0) {
          menuItem.children = convertedChildren;
        }
      }

      return menuItem;
    });
}

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: MEMBER_SERVICE_BASE_URL,
  ...requestConfig,
};
